// Sidebar Component - Redesigned
import { useState, memo, useCallback, useMemo } from "react";
import { 
  Bot, Plus, Trash2, Download, LogIn, LogOut, Settings, Coins, Crown, Wallet,
  SendHorizontal, Brain, Shield, X, MessageSquare, BarChart3,
  MoreVertical, Edit2, Share2, Archive, Copy, Check, User, Sparkles, Search, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/i18n";

const PERSONALITY_ICONS = {
  default: Bot,
  friendly: Bot,
  formal: Bot,
  funny: Bot,
  teacher: Bot
};

export function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  user,
  credits,
  isUnlimitedCredits,
  userSubscription,
  conversations,
  activeConversation,
  setActiveConversation,
  createNewConversation,
  deleteConversation,
  renameConversation,
  archiveConversation,
  shareConversation,
  exportConversation,
  showConfirm,
  logout,
  setShowAuthModal,
  setShowCreditModal,
  setShowSubscriptionModal,
  setShowTransferModal,
  setShowMemoryPanel,
  setShowProfileModal,
  setShowExchange,
  setShowPhantom,  setShowAdminPanel,
  setShowAnalyticsPanel,
  setShowDeepResearchModal,
  setShowJobsPanel,
  loadAdminData,
  currentTheme = "dark"
}) {
  const { t } = useLanguage();
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameConvId, setRenameConvId] = useState(null);
  const [newConvName, setNewConvName] = useState("");
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return conv.title?.toLowerCase().includes(query);
  });

  const handleRename = (convId, currentTitle) => {
    setRenameConvId(convId);
    setNewConvName(currentTitle);
    setShowRenameDialog(true);
  };

  const submitRename = async () => {
    if (newConvName.trim() && renameConvId) {
      await renameConversation(renameConvId, newConvName.trim());
      setShowRenameDialog(false);
      setRenameConvId(null);
      setNewConvName("");
    }
  };

  const handleShare = async (convId) => {
    const link = await shareConversation(convId);
    setShareLink(link);
    setShowShareDialog(true);
    setLinkCopied(false);
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
  };

  // Theme-based styles
  const sidebarBg = currentTheme === "light" ? "#f8fafc" : "#18181b";
  const sidebarBorder = currentTheme === "light" ? "#e2e8f0" : "#27272a";

  return (
    <aside 
      className={`sidebar ${sidebarOpen ? "open" : ""}`} 
      data-testid="sidebar"
      style={{ 
        background: sidebarBg,
        borderColor: sidebarBorder
      }}
    >
      {/* Logo Header */}
      <div className="px-4 py-5 border-b border-[var(--border-color)]">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-11 h-11 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Bot size={22} className="text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-[var(--bg-secondary)]"></div>
          </div>
          <div className="flex-1">
            <h1 className="font-bold text-lg text-[var(--text-primary)] tracking-tight">KaanGPT</h1>
            <p className="text-xs text-[var(--text-muted)]" data-testid="brand-tagline">Kaan Kann Alles!</p>
          </div>
          <Button variant="ghost" size="icon" className="md:hidden text-[var(--text-muted)]" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </Button>
        </div>
      </div>

      {/* User Section - Logged in */}
      {user && (
        <div className="px-4 py-4 border-b border-[var(--border-color)] space-y-3">
          {/* Credits */}
          {!isUnlimitedCredits && (
            <button 
              onClick={() => setShowCreditModal(true)} 
              className="w-full p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between hover:border-amber-500/50 transition-all group"
              data-testid="credits-display"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Coins size={18} className="text-amber-400" />
                </div>
                <div className="text-left">
                  <span className="text-amber-400 font-bold text-lg">{credits}</span>
                  <span className="text-[var(--text-muted)] text-xs ml-1">{t('credits')}</span>
                </div>
              </div>
              <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-1 rounded-lg group-hover:bg-amber-500/30 transition-colors">
                + {t('edit')}
              </span>
            </button>
          )}

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => setShowSubscriptionModal(true)} 
              className="p-3 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border-color)] rounded-xl flex flex-col items-center gap-1.5 transition-all"
              data-testid="subscription-btn"
            >
              <Crown size={18} className="text-purple-400" />
              <span className="text-[var(--text-secondary)] font-medium text-xs">
                {userSubscription?.status === "active" ? "Premium" : t('subscription')}
              </span>
            </button>
            <button 
              onClick={() => setShowTransferModal(true)} 
              className="p-3 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border-color)] rounded-xl flex flex-col items-center gap-1.5 transition-all"
              data-testid="transfer-btn"
            >
              <SendHorizontal size={18} className="text-emerald-400" />
              <span className="text-[var(--text-secondary)] font-medium text-xs">{t('send')}</span>
            </button>
          </div>

          {/* Deep Research CTA */}
          <button
            onClick={() => setShowDeepResearchModal && setShowDeepResearchModal(true)}
            data-testid="deep-research-btn"
            className="w-full p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/40 hover:border-purple-500/70 hover:from-purple-600/30 hover:to-pink-600/30 transition-all flex items-center gap-2.5 group"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md shadow-purple-500/40">
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="text-left flex-1">
              <div className="text-purple-200 font-semibold text-xs">{t('dr_nav_button')}</div>
              <div className="text-[10px] text-zinc-500">₺249 · 10 {t('dr_phases')}</div>
            </div>
          </button>

          {/* Deep Research Görevlerim */}
          {user && setShowJobsPanel && (
            <button
              onClick={() => setShowJobsPanel(true)}
              data-testid="dr-jobs-panel-btn"
              className="w-full p-2.5 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border-color)] flex items-center gap-2 transition-all text-xs text-[var(--text-secondary)]"
            >
              <Clock size={14} className="text-purple-400" />
              <span>{t('dr_jobs_history_title')}</span>
            </button>
          )}

          {/* Binance Exchange */}
          {user && setShowExchange && (
            <button
              onClick={() => setShowExchange(true)}
              data-testid="exchange-btn"
              className="w-full p-2.5 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border-color)] flex items-center gap-2 transition-all text-xs text-[var(--text-secondary)]"
            >
              <Coins size={14} className="text-amber-400" />
              <span>{t('exchange_nav')}</span>
            </button>
          )}

          {/* Solana Phantom wallet — hidden at user's request */}
          {false && user && setShowPhantom && (
            <button
              onClick={() => setShowPhantom(true)}
              data-testid="phantom-btn"
              className="w-full p-2.5 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border-color)] flex items-center gap-2 transition-all text-xs text-[var(--text-secondary)]"
            >
              <Wallet size={14} className="text-purple-400" />
              <span>{t('sol_nav')}</span>
            </button>
          )}

          {/* Tools Row */}
          <div className="flex gap-1.5">
            <Button 
              onClick={() => setShowMemoryPanel(true)} 
              variant="ghost" 
              className="flex-1 h-9 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            >
              <Brain size={16} className="mr-1.5" /> {t('memory')}
            </Button>
            {user.is_admin && (
              <>
                <Button 
                  onClick={() => { loadAdminData(); setShowAdminPanel(true); }} 
                  variant="ghost" 
                  size="icon"
                  className="h-9 w-9 text-purple-400 hover:bg-purple-500/10"
                  data-testid="admin-button"
                >
                  <Shield size={16} />
                </Button>
                <Button 
                  onClick={() => setShowAnalyticsPanel(true)} 
                  variant="ghost" 
                  size="icon"
                  className="h-9 w-9 text-blue-400 hover:bg-blue-500/10"
                  data-testid="analytics-button"
                >
                  <BarChart3 size={16} />
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Login Section - Not logged in */}
      {!user && (
        <div className="px-4 py-4 border-b border-[var(--border-color)]">
          <Button 
            onClick={() => setShowAuthModal(true)} 
            className="w-full bg-blue-600 hover:bg-blue-700 h-11 font-medium"
            data-testid="login-button"
          >
            <LogIn size={18} className="mr-2" />
            {t('login')}
          </Button>
          <p className="text-xs text-center text-[var(--text-muted)] mt-2">
            {t('unlimited_access')} {t('login')}
          </p>
        </div>
      )}

      {/* Conversations Section */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t('chats')}</span>
          <Button 
            onClick={createNewConversation} 
            size="sm" 
            className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-xs"
            data-testid="new-chat-btn"
          >
            <Plus size={14} className="mr-1" /> {t('new_chat')}
          </Button>
        </div>

        {/* Search Box */}
        <div className="px-3 pb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search_chats')}
              className="h-8 pl-9 pr-3 text-sm bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              data-testid="search-conversations"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="sidebar-content flex-1 overflow-y-auto px-2">
          {filteredConversations.filter(c => !c.is_archived).length === 0 ? (
            <p className="text-[var(--text-muted)] text-sm px-2 py-4 text-center">
              {searchQuery ? t('no_results') : t('no_chats')}
            </p>
          ) : (
            filteredConversations.filter(c => !c.is_archived).map((conv) => {
              const Icon = PERSONALITY_ICONS[conv.personality] || MessageSquare;
              return (
                <div 
                  key={conv.id} 
                  className={`sidebar-item group ${activeConversation?.id === conv.id ? "active" : ""}`} 
                  onClick={() => { setActiveConversation(conv); setSidebarOpen(false); }} 
                  data-testid={`conversation-${conv.id}`}
                >
                  <Icon size={16} className="text-[var(--text-muted)] flex-shrink-0" />
                  <span className="flex-1 truncate text-sm">{conv.title}</span>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="opacity-0 group-hover:opacity-100 h-7 w-7 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[var(--bg-secondary)] border-[var(--border-color)] w-44">
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleRename(conv.id, conv.title); }}
                        className="text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] cursor-pointer"
                      >
                        <Edit2 size={14} className="mr-2" />
                        {t('rename')}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleShare(conv.id); }}
                        className="text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] cursor-pointer"
                      >
                        <Share2 size={14} className="mr-2" />
                        {t('share')}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); exportConversation(); }}
                        className="text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] cursor-pointer"
                      >
                        <Download size={14} className="mr-2" />
                        {t('export')}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); archiveConversation(conv.id); }}
                        className="text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] cursor-pointer"
                      >
                        <Archive size={14} className="mr-2" />
                        {t('archive')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[var(--border-color)]" />
                      <DropdownMenuItem 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          showConfirm({
                            title: t('delete_chat_title'),
                            message: t('delete_chat_confirm'),
                            confirmText: t('yes_delete'),
                            cancelText: t('cancel'),
                            variant: "danger",
                            onConfirm: async () => {
                              await deleteConversation(null, conv.id);
                            }
                          });
                        }}
                        className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                      >
                        <Trash2 size={14} className="mr-2" />
                        {t('delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })
          )}
          
          {/* Archived */}
          {filteredConversations.filter(c => c.is_archived).length > 0 && (
            <>
              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mt-6 mb-2 px-2 flex items-center gap-2">
                <Archive size={12} />
                {t('archive')}
              </p>
              {filteredConversations.filter(c => c.is_archived).map((conv) => {
                const Icon = PERSONALITY_ICONS[conv.personality] || MessageSquare;
                return (
                  <div 
                    key={conv.id} 
                    className={`sidebar-item group opacity-60 ${activeConversation?.id === conv.id ? "active" : ""}`} 
                    onClick={() => { setActiveConversation(conv); setSidebarOpen(false); }} 
                  >
                    <Icon size={16} />
                    <span className="flex-1 truncate text-sm">{conv.title}</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="opacity-0 group-hover:opacity-100 h-7 w-7"
                      onClick={(e) => { e.stopPropagation(); archiveConversation(conv.id); }}
                      title={t('unarchive')}
                    >
                      <Archive size={14} />
                    </Button>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Bottom User Menu */}
      {user && (
        <div className="px-4 py-3 border-t border-[var(--border-color)] bg-[var(--bg-tertiary)]/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name}</p>
              <p className="text-xs text-[var(--text-muted)] truncate">{user.email}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                onClick={() => setShowProfileModal(true)} 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                data-testid="profile-btn"
              >
                <Settings size={16} />
              </Button>
              <Button 
                onClick={logout} 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10"
                data-testid="logout-button"
              >
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="bg-[var(--bg-secondary)] border-[var(--border-color)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
              <Edit2 size={18} />
              {t('rename_chat_title')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newConvName}
              onChange={(e) => setNewConvName(e.target.value)}
              placeholder={t('new_name_placeholder')}
              className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)]"
              onKeyPress={(e) => e.key === "Enter" && submitRename()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRenameDialog(false)}>{t('cancel')}</Button>
            <Button onClick={submitRename} className="bg-blue-600 hover:bg-blue-700">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-[var(--bg-secondary)] border-[var(--border-color)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
              <Share2 size={18} />
              {t('share_chat_title')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-[var(--text-secondary)]">
              {t('share_chat_desc')}
            </p>
            <div className="flex gap-2">
              <Input
                value={shareLink}
                readOnly
                className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] flex-1"
              />
              <Button onClick={copyShareLink} className={linkCopied ? "bg-green-600" : "bg-blue-600"}>
                {linkCopied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowShareDialog(false)}>{t('close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

export default memo(Sidebar);
